import { useState } from "react";
import { toast } from "sonner";
import { Building2, Handshake, TrendingUp, CheckCircle2, UploadCloud, X, Loader2 } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { fileToDataUrl } from "./dashboard/QuickAdd";
import SEO from "../components/SEO";

const AMENITY_OPTS = ["gym", "pool", "dishwasher", "in-suite laundry", "AC", "storage", "bike room", "EV charging", "balcony", "concierge", "rooftop", "sauna"];
const UTILITY_OPTS = ["heat", "water", "electricity", "internet"];

const EMPTY = {
  company_name: "", company_type: "property_management_company", contact_name: "", email: "", phone: "", website: "",
  address_street: "", unit_number: "", city: "Vancouver", neighborhood: "", beds: "", baths: "", monthly_rent: "",
  square_feet: "", floor: "", date_available: "", minimum_lease_months: "12", furnished: "na", parking: "none",
  pet_friendly: "na", accessibility: "na", accessibility_details: "", laundry: "na", promotions: "",
  description: "", consent: false,
};

export default function ForLandlords() {
  const [form, setForm] = useState(EMPTY);
  const [amenities, setAmenities] = useState([]);
  const [utilities, setUtilities] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [plans, setPlans] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [phase, setPhase] = useState("form"); // form | thanks | done
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const addFiles = async (files, target) => {
    const urls = [];
    for (const f of Array.from(files).slice(0, 8)) {
      if (!f.type.startsWith("image/")) continue;
      try { urls.push(await fileToDataUrl(f)); } catch { /* skip unreadable */ }
    }
    if (target === "photos") setPhotos((p) => [...p, ...urls].slice(0, 8));
    else setPlans((p) => [...p, ...urls].slice(0, 4));
  };

  const submit = async () => {
    if (!form.consent) return toast.error("Please agree to the privacy consent (PIPEDA) to submit.");
    if (!photos.length) return toast.error("Please add at least one photo of the home.");
    setBusy(true);
    try {
      const payload = { ...form, amenities, utilities_included: utilities, images: photos, floor_plan_images: plans };
      for (const k of ["beds", "baths", "monthly_rent", "square_feet", "floor", "minimum_lease_months"]) {
        if (payload[k] !== "" && payload[k] != null) payload[k] = Number(payload[k]);
        else delete payload[k];
      }
      delete payload.consent;
      await api.post("/pm-submissions", { ...payload, consent: true });
      setPhase("thanks");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  const resetForAnother = () => {
    setForm((f) => ({ ...EMPTY, company_name: f.company_name, company_type: f.company_type, contact_name: f.contact_name, email: f.email, phone: f.phone, website: f.website, consent: true }));
    setAmenities([]); setUtilities([]); setPhotos([]); setPlans([]);
    setPhase("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 md:py-20">
      <SEO title="For landlords & property managers — Tendly" description="List your Vancouver rentals on Tendly. Reach qualified, matched, ready-to-move renters — simple flat monthly plans, first month free." />
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-widest font-semibold text-primary/80">For landlords & property managers</p>
        <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Fill your vacancies with viewing-ready renters</h1>
        <p className="mt-5 text-muted-foreground leading-relaxed text-base md:text-lg">Tendly puts your rentals in front of qualified, concierge-matched renters — whether you own one condo or manage a full portfolio. Simple flat monthly plans, and your first month is always free.</p>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          [<Building2 key="1" className="w-6 h-6" />, "First month free", "Submit units in about a minute. Every listing is reviewed by our team before it goes live."],
          [<Handshake key="2" className="w-6 h-6" />, "Qualified leads", "Every renter we send has stated budget, timing, and preferences that match your unit."],
          [<TrendingUp key="3" className="w-6 h-6" />, "Simple flat pricing", "Plans from $50/month per listing. No lock-in — cancel anytime."],
        ].map(([icon, t, d]) => (
          <div key={t} className="rounded-2xl border border-border bg-card p-6">
            <span className="text-primary">{icon}</span>
            <h2 className="mt-3 font-heading font-semibold text-lg">{t}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{d}</p>
          </div>
        ))}
      </div>

      {/* Pricing */}
      <div className="mt-16">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-widest font-semibold text-primary/80">Pricing</p>
          <h2 className="mt-2 font-heading font-bold text-2xl sm:text-3xl tracking-tight">Simple plans for every portfolio</h2>
          <p className="mt-3 text-muted-foreground">For individual landlords and property management companies alike.</p>
          <p data-testid="pricing-banner" className="mt-4 inline-block rounded-full bg-coral-50 dark:bg-coral-800/30 text-coral-800 dark:text-coral-400 px-5 py-2 text-sm font-bold">First month free. See the traction for yourself.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { name: "Per Listing", price: "$50", per: "per listing / month", desc: "Best for 1 to 2 properties.", tid: "plan-per-listing" },
            { name: "Starter", price: "$250", per: "per month", desc: "Up to 10 active listings.", tid: "plan-starter" },
            { name: "Growth", price: "$500", per: "per month", desc: "10 to 20 active listings.", tid: "plan-growth", featured: true },
            { name: "Unlimited", price: "$1,000", per: "per month", desc: "20+ listings, flexible terms.", tid: "plan-unlimited" },
          ].map((p) => (
            <div key={p.name} data-testid={p.tid}
              className={`rounded-2xl border bg-card p-6 flex flex-col ${p.featured ? "border-primary shadow-lg ring-1 ring-primary" : "border-border shadow-sm"}`}>
              <h3 className="font-heading font-semibold text-lg">{p.name}</h3>
              <p className="mt-3 font-heading font-bold text-3xl tracking-tight">{p.price}<span className="text-sm font-medium text-muted-foreground"> {p.per}</span></p>
              <p className="mt-2 text-sm text-muted-foreground">{p.desc}</p>
              <p className="mt-2 text-xs text-muted-foreground">Qualified, concierge-matched renter leads · Human agent coordination</p>
              <a href="#submit-listing-form" data-testid={`${p.tid}-cta`}
                className={`mt-5 inline-block text-center rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${p.featured ? "bg-primary text-primary-foreground hover:bg-teal-500" : "border border-border hover:border-primary"}`}>
                Start your free month
              </a>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-sm text-muted-foreground">No checkout needed now — submit your listing and an agent will set up your plan and free month with you directly.</p>
      </div>

      <div className="mt-16 max-w-3xl" id="submit-listing-form">
        <h2 className="font-heading font-bold text-2xl tracking-tight">Submit a listing</h2>
        <p className="mt-2 text-sm text-muted-foreground">Fields marked * are required — everything else is optional and stored as N/A if left blank. Our team reviews every submission before it goes live.</p>

        {phase === "thanks" && (
          <div data-testid="pm-thanks" className="mt-6 rounded-2xl border border-border bg-card p-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-primary mx-auto" />
            <p className="mt-3 font-heading font-bold text-2xl">Thank you — listing received</p>
            <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">Your free month begins once your listing is approved, and an agent will reach out to get you set up. A confirmation email is on its way to you.</p>
            <div className="mt-6 flex gap-3 justify-center">
              <button data-testid="pm-add-another" onClick={resetForAnother}
                className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold">Add another listing</button>
              <button data-testid="pm-finish" onClick={() => setPhase("done")}
                className="rounded-full border border-border px-6 py-2.5 text-sm font-semibold">Finish</button>
            </div>
          </div>
        )}
        {phase === "done" && (
          <div data-testid="pm-done" className="mt-6 rounded-2xl border border-border bg-card p-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-primary mx-auto" />
            <p className="mt-3 font-semibold">All set.</p>
            <p className="mt-1 text-sm text-muted-foreground">Questions anytime: tendly.concierge@gmail.com · +1 (778) 956-6372</p>
            <a href="/" className="mt-5 inline-block rounded-full border border-border px-6 py-2.5 text-sm font-semibold">Back to Tendly</a>
          </div>
        )}

        {phase === "form" && (
          <div className="mt-6 rounded-2xl border border-border bg-card p-6 md:p-8 space-y-8" data-testid="pm-submit-form">

            {/* Photos */}
            <section>
              <h3 className="font-heading font-semibold">Photos * <span className="font-normal text-xs text-muted-foreground">(at least one, up to 8)</span></h3>
              <div
                data-testid="pm-photos-drop"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files, "photos"); }}
                className={`mt-3 rounded-xl border-2 border-dashed p-6 min-h-[140px] flex flex-col items-center justify-center cursor-pointer text-center transition-colors ${dragOver ? "border-primary bg-teal-50/60 dark:bg-teal-900/20" : "border-border"}`}>
                <UploadCloud className="w-6 h-6 mx-auto text-muted-foreground" />
                <p className="mt-1 text-sm font-medium">Tap to choose photos, or drag & drop here</p>
                <label className="mt-1 inline-block cursor-pointer text-sm font-semibold text-primary hover:underline">
                  or browse your device
                  <input data-testid="pm-photos-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => addFiles(e.target.files, "photos")} />
                </label>
                {photos.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2 justify-center">
                    {photos.map((u, i) => (
                      <span key={i} className="relative">
                        <img src={u} alt={`Upload ${i + 1}`} className="w-20 h-14 object-cover rounded-lg border border-border" />
                        <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} aria-label={`Remove photo ${i + 1}`} className="absolute -top-1.5 -right-1.5 bg-card border border-border rounded-full p-0.5"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-3">
                <p className="text-xs font-semibold">Floor plan <span className="font-normal text-muted-foreground">(optional)</span></p>
                <label className="mt-1 inline-flex items-center gap-2 cursor-pointer rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-secondary">
                  <UploadCloud className="w-4 h-4" /> Add floor plan image
                  <input data-testid="pm-plans-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => addFiles(e.target.files, "plans")} />
                </label>
                {plans.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {plans.map((u, i) => (
                      <span key={i} className="relative">
                        <img src={u} alt={`Floor plan ${i + 1}`} className="w-20 h-14 object-cover rounded-lg border-2 border-primary" />
                        <button onClick={() => setPlans(plans.filter((_, j) => j !== i))} aria-label={`Remove floor plan ${i + 1}`} className="absolute -top-1.5 -right-1.5 bg-card border border-border rounded-full p-0.5"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* Property */}
            <section>
              <h3 className="font-heading font-semibold mb-3">The home</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Street address *" k="address_street" form={form} set={set} tid="pm-address" />
                <Field label="Unit #" k="unit_number" form={form} set={set} tid="pm-unit" />
                <Field label="City" k="city" form={form} set={set} tid="pm-city" />
                <Field label="Neighbourhood *" k="neighborhood" form={form} set={set} tid="pm-neighborhood" />
                <Field label="Monthly rent (CAD) *" k="monthly_rent" type="number" form={form} set={set} tid="pm-rent" />
                <Field label="Bedrooms * (0 = studio)" k="beds" type="number" form={form} set={set} tid="pm-beds" />
                <Field label="Bathrooms *" k="baths" type="number" step="0.5" form={form} set={set} tid="pm-baths" />
                <Field label="Square feet" k="square_feet" type="number" form={form} set={set} tid="pm-sqft" />
                <Field label="Floor" k="floor" type="number" form={form} set={set} tid="pm-floor" />
                <Field label="Date available *" k="date_available" type="date" form={form} set={set} tid="pm-available" />
                <Select label="Minimum lease" k="minimum_lease_months" form={form} set={set} tid="pm-min-lease" opts={[["1", "1 month"], ["3", "3 months"], ["6", "6 months"], ["12", "12 months"]]} />
                <Select label="Furnished" k="furnished" form={form} set={set} tid="pm-furnished" opts={[["na", "N/A"], ["furnished", "Furnished"], ["unfurnished", "Unfurnished"], ["partially", "Partially"]]} />
                <Select label="Parking" k="parking" form={form} set={set} tid="pm-parking" opts={[["none", "None"], ["included", "Included"], ["available_extra_cost", "Available (extra cost)"]]} />
                <Select label="Pet policy" k="pet_friendly" form={form} set={set} tid="pm-pets" opts={[["na", "N/A"], ["yes", "Pet friendly"], ["no", "No pets"], ["cats_only", "Cats only"], ["dogs_only", "Dogs only"], ["case_by_case", "Case by case"]]} />
                <Select label="Accessibility" k="accessibility" form={form} set={set} tid="pm-accessibility" opts={[["na", "N/A"], ["accessible", "Accessible"], ["not_accessible", "Not accessible"]]} />
                <Select label="Laundry" k="laundry" form={form} set={set} tid="pm-laundry" opts={[["na", "N/A"], ["in_unit", "In-suite"], ["shared", "Shared"], ["none", "None"]]} />
                <Field label="Accessibility details (elevator, step-free…)" k="accessibility_details" form={form} set={set} tid="pm-accessibility-details" />
                <Field label="Promotions (e.g. 1 month free)" k="promotions" form={form} set={set} tid="pm-promotions" />
              </div>
              <div className="mt-4">
                <p className="text-xs font-semibold mb-2">Amenities <span className="font-normal text-muted-foreground">(optional, pick all that apply)</span></p>
                <div className="flex flex-wrap gap-2">
                  {AMENITY_OPTS.map((a) => (
                    <button key={a} type="button" data-testid={`pm-amenity-${a.replace(/[\s-]+/g, "-")}`} onClick={() => toggle(amenities, setAmenities, a)} aria-pressed={amenities.includes(a)}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${amenities.includes(a) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>{a}</button>
                  ))}
                </div>
              </div>
              <div className="mt-4">
                <p className="text-xs font-semibold mb-2">Utilities included <span className="font-normal text-muted-foreground">(optional)</span></p>
                <div className="flex flex-wrap gap-2">
                  {UTILITY_OPTS.map((u) => (
                    <button key={u} type="button" data-testid={`pm-utility-${u}`} onClick={() => toggle(utilities, setUtilities, u)} aria-pressed={utilities.includes(u)}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${utilities.includes(u) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>{u}</button>
                  ))}
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-xs font-semibold mb-1.5" htmlFor="pm-description">Description</label>
                <textarea id="pm-description" data-testid="pm-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3}
                  className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm" placeholder="What makes this home great?" />
              </div>
            </section>

            {/* Contact */}
            <section>
              <h3 className="font-heading font-semibold mb-3">Who manages it</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Company or landlord name *" k="company_name" form={form} set={set} tid="pm-company-name" />
                <Select label="You are a…" k="company_type" form={form} set={set} tid="pm-company-type" opts={[["property_management_company", "Property management company"], ["individual_landlord", "Individual landlord"]]} />
                <Field label="Contact name *" k="contact_name" form={form} set={set} tid="pm-contact-name" />
                <Field label="Contact email *" k="email" type="email" form={form} set={set} tid="pm-email" />
                <Field label="Contact phone" k="phone" form={form} set={set} tid="pm-phone" />
                <Field label="Website" k="website" form={form} set={set} tid="pm-website" />
              </div>
            </section>

            <label className="flex items-start gap-2.5 text-xs text-muted-foreground leading-relaxed cursor-pointer">
              <input data-testid="pm-consent" type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 accent-[#0D5C63]" />
              <span>* I agree to Tendly contacting me about this listing and getting set up, and I've read the <a href="/privacy" target="_blank" className="text-primary underline">Privacy Policy</a>.</span>
            </label>

            <button data-testid="pm-submit" onClick={submit} disabled={busy}
              className="rounded-full bg-accent text-accent-foreground px-8 py-3 font-semibold hover:bg-coral-600 transition-colors disabled:opacity-50 inline-flex items-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit listing for review
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, k, form, set, type = "text", tid, step }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5" htmlFor={tid}>{label}</label>
      <input id={tid} data-testid={tid} type={type} step={step} value={form[k]} onChange={(e) => set(k, e.target.value)}
        className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm" />
    </div>
  );
}

function Select({ label, k, form, set, opts, tid }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5" htmlFor={tid}>{label}</label>
      <select id={tid} data-testid={tid} value={form[k]} onChange={(e) => set(k, e.target.value)}
        className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm">
        {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
